/**
 * Variant Option Filter for Custom Hex Dumbbell Set Builder
 * Specifically targets the Custom Hex Dumbbell Set Builder With Optional Rack product
 * to hide Storage Rack options when they're not available for certain dumbbell sets
 */

if (!customElements.get("variant-option-filter")) {
  class VariantOptionFilter extends HTMLElement {
    constructor() {
      super();
      this.productData = null;
      this.currentSelections = new Map();
      this.targetProductTitle = "Custom Hex Dumbbell Set Builder With Optional Rack";
      this.init();
    }

    init() {
      // Only run for the specific target product
      if (!this.isTargetProduct()) {
        return;
      }

      // Get product data from the page
      this.loadProductData();
      
      // Listen for variant changes from the variant picker
      this.addEventListener('change', this.handleVariantChange.bind(this));
      
      // Listen for the theme's variant change events
      document.addEventListener('variant:changed', this.handleThemeVariantChange.bind(this));
      
      // Initial filter
      setTimeout(() => {
        this.filterOptions();
      }, 100);
    }

    isTargetProduct() {
      // Check if this is the target product by looking for the product title
      const productTitle = document.querySelector('h1')?.textContent?.trim();
      return productTitle === this.targetProductTitle;
    }

    loadProductData() {
      const productId = this.dataset.productId;
      if (productId && window._themeProducts && window._themeProducts[productId]) {
        this.productData = window._themeProducts[productId];
        
        // Initialize current selections from the default variant
        if (this.productData.selected_or_first_available_variant) {
          this.updateSelectionsFromVariant(this.productData.selected_or_first_available_variant);
        }
      }
    }

    handleVariantChange(event) {
      // Update current selections from the change event
      const target = event.target;
      const optionName = target.name || target.dataset.fieldName;
      let optionValue = target.value || target.dataset.optionValueId;
      
      // Handle select elements
      if (target.tagName === 'SELECT') {
        const selectedOption = target.options[target.selectedIndex];
        optionValue = selectedOption ? selectedOption.value : optionValue;
      }
      
      if (optionName && optionValue) {
        this.currentSelections.set(optionName, optionValue);
      }
      
      // Re-filter options after a short delay to allow DOM updates
      setTimeout(() => {
        this.filterOptions();
      }, 100);
    }

    handleThemeVariantChange(event) {
      // Update selections based on the new variant
      const variant = event.detail.variant;
      if (variant && this.productData) {
        this.updateSelectionsFromVariant(variant);
        setTimeout(() => {
          this.filterOptions();
        }, 100);
      }
    }

    updateSelectionsFromVariant(variant) {
      if (!variant || !this.productData) return;
      
      // Clear current selections
      this.currentSelections.clear();
      
      // Update with new variant's options
      variant.options.forEach((optionValue, index) => {
        if (this.productData.options[index]) {
          const optionName = this.productData.options[index].name;
          this.currentSelections.set(optionName, optionValue);
        }
      });
    }

    filterOptions() {
      if (!this.productData) return;

      const options = this.querySelectorAll('.m-product-option');
      
      options.forEach((optionElement, optionIndex) => {
        const optionNameElement = optionElement.querySelector('[data-option-name]');
        const optionName = optionNameElement?.dataset.optionName;
        if (!optionName) return;

        // Special handling for Storage Rack option
        if (optionName.toLowerCase().includes('storage rack') || optionName.toLowerCase().includes('rack')) {
          this.filterStorageRackOption(optionElement, optionIndex);
        }
      });
    }

    filterStorageRackOption(optionElement, optionIndex) {
      // Get current dumbbell set selection
      const dumbbellSetSelection = this.getDumbbellSetSelection();
      
      // Get available storage rack options for this dumbbell set
      const availableRackOptions = this.getAvailableStorageRackOptions(dumbbellSetSelection);
      
      // Hide storage rack option if no options are available
      if (availableRackOptions.length === 0) {
        optionElement.style.display = 'none';
      } else {
        optionElement.style.display = '';
        this.updateStorageRackOptions(optionElement, availableRackOptions);
      }
    }

    getDumbbellSetSelection() {
      // Find the dumbbell set option and get its current value
      const dumbbellSetOption = this.querySelector('.m-product-option:not([style*="display: none"]) [data-option-name*="Dumbbell Set"], .m-product-option:not([style*="display: none"]) [data-option-name*="dumbbell set"]');
      if (dumbbellSetOption) {
        const optionName = dumbbellSetOption.dataset.optionName;
        return this.currentSelections.get(optionName);
      }
      return null;
    }

    getAvailableStorageRackOptions(dumbbellSetSelection) {
      if (!this.productData || !this.productData.variants) return [];

      const availableRackOptions = new Set();
      
      this.productData.variants.forEach(variant => {
        // Check if this variant matches the current dumbbell set selection
        if (this.matchesDumbbellSetSelection(variant, dumbbellSetSelection)) {
          // Get the storage rack option value
          const rackOptionValue = this.getStorageRackOptionValue(variant);
          if (rackOptionValue) {
            availableRackOptions.add(rackOptionValue);
          }
        }
      });

      return Array.from(availableRackOptions).sort();
    }

    matchesDumbbellSetSelection(variant, dumbbellSetSelection) {
      if (!dumbbellSetSelection) return true;
      
      // Find the dumbbell set option index
      const dumbbellSetOptionIndex = this.getDumbbellSetOptionIndex();
      if (dumbbellSetOptionIndex === -1) return true;
      
      // Check if variant matches the dumbbell set selection
      return variant.options[dumbbellSetOptionIndex] === dumbbellSetSelection;
    }

    getDumbbellSetOptionIndex() {
      if (!this.productData || !this.productData.options) return -1;
      
      for (let i = 0; i < this.productData.options.length; i++) {
        const optionName = this.productData.options[i].name;
        if (optionName.toLowerCase().includes('dumbbell set')) {
          return i;
        }
      }
      return -1;
    }

    getStorageRackOptionValue(variant) {
      // Find the storage rack option index
      const rackOptionIndex = this.getStorageRackOptionIndex();
      if (rackOptionIndex === -1) return null;
      
      return variant.options[rackOptionIndex];
    }

    getStorageRackOptionIndex() {
      if (!this.productData || !this.productData.options) return -1;
      
      for (let i = 0; i < this.productData.options.length; i++) {
        const optionName = this.productData.options[i].name;
        if (optionName.toLowerCase().includes('storage rack') || optionName.toLowerCase().includes('rack')) {
          return i;
        }
      }
      return -1;
    }

    updateStorageRackOptions(optionElement, availableRackOptions) {
      // Update select options or radio buttons to only show available values
      const select = optionElement.querySelector('select');
      const radioButtons = optionElement.querySelectorAll('input[type="radio"]');
      
      if (select) {
        Array.from(select.options).forEach(option => {
          const isAvailable = availableRackOptions.includes(option.value);
          option.disabled = !isAvailable;
          option.style.display = isAvailable ? '' : 'none';
        });
      }
      
      if (radioButtons.length > 0) {
        radioButtons.forEach(radio => {
          const isAvailable = availableRackOptions.includes(radio.value);
          radio.disabled = !isAvailable;
          
          // Find the parent container to hide/show
          const parentContainer = radio.closest('.m-product-option--node') || radio.parentElement;
          if (parentContainer) {
            parentContainer.style.display = isAvailable ? '' : 'none';
          }
        });
      }
    }
  }

  customElements.define("variant-option-filter", VariantOptionFilter);
}
